import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthProvider";
import loginBg from "../../assets/login-bg.jpg";
import testimonialAvatar from "../../assets/testimonial-avatar.jpg";
import google from "../../assets/google.png";
import logo from "../../assets/logo.jpg";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "react-fox-toast";
import { getApiBaseUrl } from '../../config/api';

// Get API base URL from centralized config
const API_BASE_URL = getApiBaseUrl();

export const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { signup, resendVerificationEmail, googleSignIn, logout } = useAuth();
  const navigate = useNavigate();

  // Password validation function
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid:
        password.length >= minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumber &&
        hasSpecialChar,
      requirements: {
        minLength: password.length >= minLength,
        hasUpperCase,
        hasLowerCase,
        hasNumber,
        hasSpecialChar,
      },
    };
  };

  const setupUserWithBackend = async (user) => {
    try {
      const authToken = await user.getIdToken();
      const userId = user.uid;
      console.log("User ID:", userId);
      console.log("Auth Token:", authToken);

      const response = await fetch(
        `${API_BASE_URL}/auth/setup-user`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-User-Id": userId,
          },
        }
      );

      console.log("Response from backend:", response);

      if (!response.ok) {
        throw new Error(
          `API error: ${response.status} - ${await response.text()}`
        );
      }

      const data = await response.json();
      
      // Add a small delay to ensure backend has fully processed the user setup
      // This is especially important for new users where privileges need to be created
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return data;
    } catch (error) {
      console.error("Error setting up user with backend:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const passwordValidationResult = validatePassword(password);
    if (!passwordValidationResult.isValid) {
      toast.error(
        "Password must meet all requirements: 8+ chars, 1 uppercase, 1 lowercase, 1 number, 1 special char."
      );
      return;
    }

    try {
      setLoading(true);
      const displayName = `${firstName} ${lastName}`.trim();
      const userCredential = await signup(email, password, displayName);
      await setupUserWithBackend(userCredential.user);

      toast.success("Account created! Please check your email to verify.");
      setEmailSent(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        toast.warning(
          "This email is already registered. Please log in or use a different email."
        );
      } else {
        toast.error(
          "Failed to create an account: " +
            (err.message || "An unknown error occurred.")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await resendVerificationEmail();
      toast.success(
        "Verification email resent successfully! Please check your inbox."
      );
    } catch (err) {
      toast.error(
        "Failed to resend verification email: " +
          (err.message || "An unknown error occurred.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const userCredential = await googleSignIn();
      await setupUserWithBackend(userCredential.user);
      
      toast.success("Successfully signed in with Google!");
      navigate("/recentactivities");
    } catch (err) {
      toast.error(
        "Failed to sign in with Google: " +
          (err.message || "An unknown error occurred.")
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailSent) {
      logout();
      window.history.pushState(null, "", window.location.pathname);
      const handleBackButton = (e) => {
        if (emailSent) {
          e.preventDefault();
          navigate("/login");
        }
      };

      window.addEventListener("popstate", handleBackButton);
      return () => window.removeEventListener("popstate", handleBackButton);
    }
  }, [emailSent, logout, navigate]);

  const avatarPlaceholder = "/placeholder.svg?height=40&width=40";

  if (emailSent) {
    return (
      <div className=" flex w-full">
        {/* Left Side - Dark Background with Social Media Preview */}
        <div className="flex-1 bg-black max-w-xl overflow-y-hidden fixed flex flex-col justify-center items-center relative">
          <img src={loginBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          {/* Testimonial Card */}
          <div className="absolute bottom-8 left-4 right-4 z-10 w-full max-w-md bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-lg">
            <div className="p-6">
              <p className="text-white text-sm leading-relaxed mb-4">
                {"I've used countless image tools, but nothing matches the precision and speed of this AI editor. Backgrounds, upscaling, even virtual try-ons—it just works. It's now a core part of my workflow."}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-600 flex justify-center items-center overflow-hidden">
                  <img src={testimonialAvatar} alt="Anaya K." className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">Anaya K.</div>
                  <div className="text-gray-400 text-xs">Founder of a Growing SaaS Startup</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Verification Message */}
        <div className="flex-1 bg-white p-8 flex flex-col justify-center items-center">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center">
                <span className="text-gray-900 font-bold text-xl">
                  <img src={logo} alt="" />
                </span>
              </div>
            </div>

            {/* Welcome Text */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Verify Your Email
              </h1>
              <p className="text-gray-500">
                Check your inbox to complete registration
              </p>
            </div>

            {/* Verification Message */}
            <div className="bg-violet-900/30 border border-violet-600/30 text-violet-300 px-4 py-3 rounded-lg mb-6">
              We've sent a verification email to{" "}
              <span className="font-semibold">{email}</span>. Please check your
              inbox and click the verification link to activate your account.
            </div>

            <div className="text-center text-sm text-gray-500 mb-4">
              Didn't receive the email?
              <button
                onClick={handleResendVerification}
                className="ml-1 font-medium text-violet-600 hover:text-violet-300 focus:outline-none"
                disabled={loading}
              >
                {loading ? "Resending..." : "Resend verification email"}
              </button>
            </div>

            <div className="text-center text-sm text-gray-500">
              Already verified?
              <Link
                to="/login"
                className="ml-1 font-medium text-violet-600 hover:text-violet-300"
              >
                Log in here
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" h-[105vh] flex w-full ">
      {/* Left Side - Dark Background with Social Media Preview */}
      <div className="flex-1 bg-black max-w-xl md:flex hidden h-full flex-col justify-center items-center relative overflow-hidden">
        <img src={loginBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        {/* Testimonial Card */}
        <div className="absolute bottom-8 left-4 right-4 z-10 w-full max-w-md bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-lg">
          <div className="p-6">
            <p className="text-white text-sm leading-relaxed mb-4">
              {"I've used countless image tools, but nothing matches the precision and speed of this AI editor. Backgrounds, upscaling, even virtual try-ons—it just works. It's now a core part of my workflow."}
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-600 flex justify-center items-center overflow-hidden">
                <img src={testimonialAvatar} alt="Anaya K." className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">Anaya K.</div>
                <div className="text-gray-400 text-xs">Founder of a Growing SaaS Startup</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Dark Background with Sign Up Form */}
      <div className="flex-1 bg-white p-8 w-[60vw] flex flex-col justify-center items-center">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center">
            <div className="md:w-20 md:h-20 w-24 h-24 mx-auto flex items-center justify-center">
              <span className="text-gray-900 font-bold text-xl">
                <img src={logo} alt="" />
              </span>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-6">
            <p className="text-gray-500">Sign up to start your design journey</p>
          </div>

          {/* Google Sign Up Button */}
          <button
            type="button"
            className="w-full mb-6 h-12 border border-gray-300 bg-white rounded-md hover:bg-gray-50 flex items-center justify-center gap-3 text-gray-300 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-white disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5">
                  <img src={google} alt="" />
                </div>
                <span className="text-gray-700 font-medium">
                  Sign up with Google
                </span>
              </div>
            )}
          </button>

          {/* Divider */}
          <div className="text-center flex items-center justify-center gap-3 mb-4">
            <div className="w-[10vw] h-[0.5px] bg-gray-300"></div>
            <span className="text-gray-500">Or Sign up with</span>
            <div className="w-[10vw] h-[0.5px] bg-gray-300"></div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Name Inputs */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="first-name"
                  className="block text-gray-700 font-medium mb-2"
                >
                  First Name
                </label>
                <input
                  id="first-name"
                  name="first-name"
                  type="text"
                  autoComplete="given-name"
                  required
                  className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="last-name"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Last Name
                </label>
                <input
                  id="last-name"
                  name="last-name"
                  type="text"
                  autoComplete="family-name"
                  required
                  className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            {/* Email Input */}
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-gray-700 font-medium mb-2"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Password Input */}
            <div className="mb-4">
              <label
                htmlFor="password"
                className="block text-gray-700 font-medium mb-2"
              >
                Password
                <span className="text-xs text-gray-500 ml-1">
                  (must meet all requirements below)
                </span>
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {/* Confirm Password Input */}
            <div className="mb-6">
              <label
                htmlFor="confirm-password"
                className="block text-gray-700 font-medium mb-2"
              >
                Confirm Password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {/* Sign Up Button */}
            <button
              className="w-full h-12 bg-black hover:bg-gray-900 text-white rounded-lg mb-6 font-medium transition-all focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="ml-2 h-4 w-4 inline" />
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="text-center">
            <span className="text-gray-500">Already have an account? </span>
            <Link
              to="/login"
              className="text-violet-600 p-0 h-auto font-normal hover:underline focus:outline-none focus:underline"
            >
              Log in
            </Link>
          </div>
          {/* term and privacy */}
          <div className="text-center">
            <span className="text-gray-500">By signing up you agree to our </span>
            <Link
              to="/terms-and-privacy" target="_blank"
              className="text-violet-600 p-0 h-auto font-normal hover:underline focus:outline-none focus:underline"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;